import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Calendar, ArrowLeft, Tag } from "lucide-react";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  published_at: string | null;
  created_at: string;
  category: string | null;
  tags: string[] | null;
}

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .eq("published", true)
      .single();

    if (error || !data) {
      toast({
        title: "Error",
        description: "Post not found",
        variant: "destructive",
      });
      navigate("/blog");
    } else {
      setPost(data);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <>
      <Navigation />
      <main className="min-h-screen pt-28 py-12 px-4">
      <article className="container mx-auto max-w-4xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/blog")}
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Button>

        {/* Header */}
        <header className="space-y-6 mb-12">
          <div className="flex items-center gap-2">
            {post.category && <Badge variant="secondary">{post.category}</Badge>}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-xl text-muted-foreground">{post.excerpt}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-muted-foreground border-b pb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.published_at
                ? format(new Date(post.published_at), "MMMM d, yyyy")
                : format(new Date(post.created_at), "MMMM d, yyyy")}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-12">
          <div className="whitespace-pre-wrap leading-relaxed text-foreground">
            {post.content}
          </div>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="border-t pt-8">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 bg-primary/5 border-2 border-primary/20 rounded-2xl text-center space-y-4">
          <h3 className="text-2xl font-bold">Ready to Transform Your Business?</h3>
          <p className="text-muted-foreground">
            Join Launch To Revenue and start your journey to success
          </p>
          <Button size="lg" onClick={() => navigate("/auth")}>
            Get Started Today
          </Button>
        </div>
      </article>
    </main>
    </>
  );
};

export default BlogPost;
